import { zodResolver } from '@hookform/resolvers/zod'
import React, { useEffect, useState } from 'react'
import { Button, Form, Modal } from 'react-bootstrap'
import { Controller, useForm } from 'react-hook-form'
import { customStyles } from '../Utils/helpers/commonhelper'
import useLocalStorageState from 'use-local-storage-state'
import { GetRequest, UpdateRF } from '../Utils/hooks/ApiHandler'
import Select from "react-select"
import { useNavigate } from 'react-router-dom'
import z from 'zod'
import { toast } from 'sonner'
import { usePostRequest } from '../Utils/hooks/QueryHandler'

const productSchema = z
    .object({
        title: z.string().min(1, "Title is required"),
        price: z
            .string()
            .min(1, "Price is required")
            .refine((val) => !isNaN(Number(val)) && Number(val) >= 0, {
                message: "Price must be a valid number and at least 0",
            }),
        gst: z.string().min(1, "GST is Required!"),
        hascCode: z.string().min(1, "HASC Code is required"),
        category: z.enum(["Product", "Service"]).optional(),
        unit: z.string().optional(),
    })
    .superRefine((data, ctx) => {
        if (
            data.category === "Product" &&
            (!data.unit || data.unit.trim() === "")
        ) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Unit is required when category is Product",
                path: ["unit"],
            })
        }
    })
export const CreateProductsModal = ({ showModal, setShowModal, products, fetchProducts, productFieldsRef }) => {

    const [customer, setCustomer] = useLocalStorageState('customer');
    const customer_id = customer.customer_details?.customer_id
    const loginUserId = customer.customer_details?.id
    const token = customer?.token?.authorization
    const rf = customer?.token?.authentication
    const headers = {
        Authorization: token,
        Authentication: rf,
    }
    const navigate = useNavigate()
    const extras = { customer, setCustomer, rf, navigate }

    // const [products, setProducts] = useState([])
    const [latestProductId, setLatestProductId] = useState(null)

    // for product and services form
    const {
        register: registerProduct,
        handleSubmit: handleSubmitProduct,
        reset: resetProduct,
        control: controlProduct,
        watch: watchProduct,
        formState: { errors: productErrors },
    } = useForm({
        resolver: zodResolver(productSchema),
        defaultValues: {
            title: "",
            price: "",
            gst: "5",
            hascCode: "",
            category: "Product", // Default value
            unit: "",
        },
    })


    // Updated useEffect for handling new products
    useEffect(() => {
        if (latestProductId && products.length > 0 && productFieldsRef.current) {
            const selectedProduct = products.find(
                (p) => String(p.id) === String(latestProductId),
            )
            if (selectedProduct) {
                productFieldsRef.current.addNewProductFromModal(
                    latestProductId,
                    selectedProduct.price,
                )
                setLatestProductId(null)
            }
        }
    }, [products, latestProductId])

    const { mutate: addProduct } = usePostRequest(
        `/masters/createProduct`,
        null,
        { headers },
        extras,
    )

    // Handle product form submission
    const onProductSubmit = async (data) => {
        const payload = {
            customer_id,
            service: data.title,
            tax: data.gst,
            unit: data.unit || "",
            price: data.price,
            hsn_sac: data.hascCode,
            description: "",
            category: data.category || "Product",
            gst: data.gst,
            login_user_id: loginUserId,
        }

        addProduct(payload, {
            onSuccess: (res) => {
                if (res.status === "fail") {
                    toast.error(res.message)
                    return
                }
                if (res.status === "error") return
                if (res.status === "Expire") {
                    UpdateRF(headers.Authentication)
                    navigate("/login")
                    return
                }

                toast.success(res.message || "Product added successfully")
                const newProductId = res.data
                if (newProductId) {
                    setLatestProductId(Number(newProductId))
                }
                setShowModal(false)
                resetProduct()
                fetchProducts()
            },
            onError: (err) => {
                toast.error(err?.message)
            },
        })
    }
    return (
        <Modal show={showModal} onHide={() => setShowModal(false)} centered>
            <Modal.Header closeButton>
                <Modal.Title>Add Product or Services</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form
                    className="form-container"
                    onSubmit={handleSubmitProduct(onProductSubmit)}
                >
                    <Form.Group className="mb-2 position-relative focus-z-index">
                        <Form.Label className="w-auto form-label">
                            Lead Product or Services Title
                            <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Product or Services Title"
                            {...registerProduct("title")}
                            isInvalid={!!productErrors.title}
                        />
                        <Form.Control.Feedback type="invalid">
                            {productErrors.title?.message}
                        </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-2 position-relative focus-z-index">
                        <label className="w-auto focus-label">
                            Price <span className="text-danger">*</span>
                        </label>
                        <Form.Control
                            type="number"
                            step="0.01"
                            placeholder="Enter price"
                            {...registerProduct("price")}
                            isInvalid={!!productErrors.price}
                        />
                        <Form.Control.Feedback type="invalid">
                            {productErrors.price?.message}
                        </Form.Control.Feedback>
                    </Form.Group>
                    <div className="mb-2 d-flex flex-column focus-z-index position-relative">
                        <label className="w-auto">GST %</label>
                        <Controller
                            name="gst"
                            control={controlProduct}
                            render={({ field }) => (
                                <Select
                                    options={[
                                        { value: "0", label: "0%" },
                                        { value: "5", label: "5%" },
                                        { value: "10", label: "10%" },
                                        { value: "18", label: "18%" },
                                        { value: "24", label: "24%" },
                                    ]}
                                    styles={customStyles}
                                    classNamePrefix="react-select"
                                    placeholder="Select GST %"
                                    isClearable={true}
                                    value={
                                        field.value
                                            ? { value: field.value, label: `${field.value}%` }
                                            : { value: "5", label: "5%" }
                                    }
                                    onChange={(selectedOption) => {
                                        field.onChange(selectedOption?.value ?? "5")
                                    }}
                                />
                            )}
                        />
                        {productErrors.gst && (
                            <small className="text-danger">
                                {productErrors.gst?.message}
                            </small>
                        )}
                    </div>

                    <Form.Group className="mb-2 focus-z-index position-relative">
                        <label className="w-auto form-label">
                            HSN or SAC Code <span className="text-danger">*</span>
                        </label>
                        <Form.Control
                            type="text"
                            placeholder="Enter HSN or SAC Code"
                            {...registerProduct("hascCode")}
                            isInvalid={!!productErrors.hascCode}
                        />
                        <Form.Control.Feedback type="invalid">
                            {productErrors.hascCode?.message}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <div className="mb-3 d-flex flex-column">
                        <span
                            style={{
                                color: "#878b8e",
                                fontSize: "0.86rem",
                                fontWeight: "400",
                                marginBottom: "7px",
                            }}
                        >
                            Category
                        </span>
                        <div className="d-flex gap-4">
                            <div className="d-flex align-items-end gap-2">
                                <Form.Check
                                    type="radio"
                                    value="Product"
                                    id="category-product"
                                    {...registerProduct("category")}
                                    className="custom-radio"
                                    style={{ cursor: "pointer" }}
                                />
                                <span
                                    style={{
                                        color: "#878b8e",
                                        fontSize: "0.86rem",
                                        fontWeight: "400",
                                    }}
                                >
                                    Product
                                </span>
                            </div>
                            <style jsx>
                                {`
                          .custom-radio .form-check-input {
                            width: 24px;
                            height: 24px;
                            background-color: var(--custom-white);
                            border: 1px solid var(--input-border);
                          }
                          .custom-radio .form-check-input:checked {
                            width: 24px;
                            height: 24px;
                            background-color: var(--primary-color);
                            border-color: var(--primary-color);
                          }
                        `}
                            </style>
                            <div className="d-flex align-items-end gap-2">
                                <Form.Check
                                    type="radio"
                                    value="Service"
                                    id="category-service"
                                    {...registerProduct("category")}
                                    className="custom-radio"
                                    style={{ cursor: "pointer" }}
                                />
                                <span
                                    style={{
                                        color: "#878b8e",
                                        fontSize: "0.86rem",
                                        fontWeight: "400",
                                    }}
                                >
                                    Service
                                </span>
                            </div>
                        </div>
                        {productErrors.category && (
                            <small className="text-danger d-block">
                                {productErrors.category.message}
                            </small>
                        )}
                    </div>

                    <div className="mb-3 d-flex flex-column focus-z-index position-relative">
                        <label className="form-label">
                            Units
                            {watchProduct("category") === "Product" && (
                                <span className="text-danger">*</span>
                            )}
                        </label>
                        <Controller
                            name="unit"
                            control={controlProduct}
                            render={({ field }) => (
                                <Select
                                    options={[
                                        { value: "Kilogram(Kgs)", label: "Kilogram(Kgs)" },
                                        { value: "Grams(Gms)", label: "Grams(Gms)" },
                                        { value: "Liters(L)", label: "Liters(L)" },
                                        {
                                            value: "Milliliters(Ml)",
                                            label: "Milliliters(Ml)",
                                        },
                                        { value: "Meters(M)", label: "Meters(M)" },
                                        {
                                            value: "Centimeters(Cm)",
                                            label: "Centimeters(Cm)",
                                        },
                                        { value: "Dozen(Dz)", label: "Dozen(Dz)" },
                                        { value: "Pounds(Lb)", label: "Pounds(Lb)" },
                                        { value: "Feet(Ft)", label: "Feet(Ft)" },
                                        { value: "Inches(In)", label: "Inches(In)" },
                                        { value: "Kilometer(Km)", label: "Kilometer(Km)" },
                                        { value: "Box(B)", label: "Box(B)" },
                                        { value: "Pieces(P)", label: "Pieces(P)" },
                                    ]}
                                    styles={customStyles}
                                    classNamePrefix="react-select"
                                    placeholder={
                                        watchProduct("category") === "Product"
                                            ? "Select unit*"
                                            : "Optional"
                                    }
                                    isClearable={true}
                                    value={
                                        field.value
                                            ? { value: field.value, label: field.value }
                                            : null
                                    }
                                    onChange={(selectedOption) => {
                                        field.onChange(selectedOption?.value ?? "")
                                    }}
                                />
                            )}
                        />
                        {productErrors.unit &&
                            watchProduct("category") === "Product" && (
                                <small className="text-danger">
                                    {productErrors.unit.message}
                                </small>
                            )}
                    </div>

                    <div className="d-flex gap-3 mt-4 border-top-2 justify-content-end align-items-center">
                        <Button
                            className="btn btn-default-outline"
                            onClick={() => {
                                setShowModal(false)
                                resetProduct()
                            }}
                        >
                            Cancel
                        </Button>
                        <Button className="btn btn-default" type="submit">
                            Save
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    )
}